import { useQueryHelper, useSelectItem } from '@anytag/shared-hooks';
import { AnchorProps, TableCell } from '@anytag/shared-atoms';
import { YoutubeCmsInvoice, useCmsInvoiceDownloadUrlPromiseQuery } from '@anytag/graphql';
import { downloadFileFromUrl, formatIntNumber, localizedDateFormatter } from '@anytag/shared-utils';
import { ColumnDef } from '@tanstack/react-table';

export const useYoutubeCMSInvoicesTable = () => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { selectedItem, setSelectedItem } = useSelectItem<number>(null);
  const { getCmsInvoiceDownloadUrl } = useCmsInvoiceDownloadUrlPromiseQuery();

  const handleDownloadInvoice =
    (invoiceId: number): AnchorProps['onClick'] =>
    async (e) => {
      e.preventDefault();
      setSelectedItem(invoiceId);

      try {
        const { data } = await getCmsInvoiceDownloadUrl({ variables: { id: invoiceId } });

        const url = data.cmsInvoiceDownloadUrl?.url;

        if (url) {
          downloadFileFromUrl(url, `invoice-${invoiceId}.pdf`);
        } else {
          enqueueSnackbar(t('Failed to download invoice'), { variant: 'error' });
        }
      } catch (error) {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      } finally {
        setSelectedItem(null);
      }
    };

  const columns: ColumnDef<YoutubeCmsInvoice>[] = [
    {
      accessorKey: 'influencerName',
      header: t<string>('HeaderColumn.AnyCreator name'),
      cell: ({ row }) => <TableCell.Text className="min-w-0" value={row.original.influencerName} />
    },
    {
      accessorKey: 'name',
      header: t<string>('HeaderColumn.Invoice name'),
      cell: ({ row }) => <TableCell.Text className="min-w-0" value={row.original.name} />
    },
    {
      accessorKey: 'paymentTotal',
      header: t<string>('HeaderColumn.Payment total'),
      cell: ({ row }) => (
        <TableCell.Text
          css={{ minWidth: 'unset' }}
          value={
            <>
              <span css={{ fontSize: '12px' }}>{formatIntNumber(row.original.paymentTotal)}</span>
              <span css={{ fontSize: '10px' }}>{t(row.original.currency)}</span>
            </>
          }
        />
      )
    },
    {
      accessorKey: 'invoice',
      header: t<string>('HeaderColumn.Invoice'),
      cell: ({ row }) => (
        <TableCell.Link
          variant="blue"
          disabled={!!selectedItem}
          className="min-w-25"
          onClick={handleDownloadInvoice(row.original.id)}
          label={t(selectedItem === row.original.id ? 'Button.Downloading' : 'Button.Download')}
        />
      )
    },
    {
      accessorKey: 'createdDate',
      header: t<string>('HeaderColumn.Created date'),
      cell: ({ row }) => (
        <TableCell.Text
          className="min-w-0"
          value={row.original.createdDate ? localizedDateFormatter(row.original.createdDate, 'yyyy/MM/dd') : '-'}
        />
      )
    },
    {
      accessorKey: 'approvedDate',
      header: t<string>('HeaderColumn.Approve date'),
      cell: ({ row }) => (
        <TableCell.Text
          className="min-w-0"
          value={row.original.approvedDate ? localizedDateFormatter(row.original.approvedDate, 'yyyy/MM/dd') : '-'}
        />
      )
    }
  ];

  return { columns };
};
